import { useEffect, useRef } from 'react';
import { isValidImage } from '../utils/imageValidation';

/**
 * Hook para monitorar referências de Image e detectar problemas
 * Solução 8: Monitoramento e logging para diagnóstico
 * 
 * @param {Array} uploadedImages - Array de imagens
 * @param {string} selectedImageId - ID da imagem selecionada
 * @param {number} interval - Intervalo de verificação em ms (padrão: 10000 = 10s)
 */
export function useImageReferenceMonitor(uploadedImages, selectedImageId, interval = 10000) {
  const previousStateRef = useRef({
    timestamp: Date.now(),
    images: new Map(),
    warnings: []
  });

  useEffect(() => {
    if (!uploadedImages || uploadedImages.length === 0) {
      return;
    }

    const monitorInterval = setInterval(() => {
      const now = Date.now();
      const currentState = {
        timestamp: now,
        images: new Map(),
        warnings: [],
        errors: []
      };

      // Monitorar todas as imagens
      uploadedImages.forEach(img => {
        const imageState = {
          id: img.id,
          hasImage: !!img.image,
          isValid: false,
          isComplete: false,
          hasDimensions: false,
          hasSrc: !!img.src,
          hasOriginalSrc: !!img.originalSrc,
          imageType: typeof img.image,
          isHTMLImageElement: img.image instanceof HTMLImageElement
        };

        if (img.image) {
          if (img.image instanceof HTMLImageElement) {
            imageState.isValid = isValidImage(img.image);
            imageState.isComplete = img.image.complete;
            imageState.hasDimensions = 
              img.image.naturalWidth > 0 && 
              img.image.naturalHeight > 0;
            
            if (!imageState.isValid) {
              currentState.warnings.push({
                type: 'invalid_image',
                imageId: img.id,
                message: `Imagem ${img.id} tem referência inválida`,
                details: {
                  complete: imageState.isComplete,
                  width: img.image.naturalWidth,
                  height: img.image.naturalHeight
                }
              });
            }
          } else {
            currentState.errors.push({
              type: 'wrong_type',
              imageId: img.id,
              message: `Imagem ${img.id} não é HTMLImageElement`,
              details: {
                type: imageState.imageType,
                constructor: img.image?.constructor?.name
              }
            });
          }
        } else {
          // Sem referência de image
          if (img.src || img.originalSrc) {
            currentState.warnings.push({
              type: 'missing_reference',
              imageId: img.id,
              message: `Imagem ${img.id} sem referência de Image, mas tem src`,
              details: {
                hasSrc: !!img.src,
                hasOriginalSrc: !!img.originalSrc
              }
            });
          } else {
            currentState.errors.push({
              type: 'no_reference_no_src',
              imageId: img.id,
              message: `Imagem ${img.id} sem referência de Image e sem src`,
            });
          }
        }

        currentState.images.set(img.id, imageState);
      });

      // Comparar com estado anterior para detectar mudanças
      const previousState = previousStateRef.current;
      
      // Verificar se alguma imagem válida se tornou inválida
      previousState.images.forEach((prevState, imageId) => {
        const currentImageState = currentState.images.get(imageId);
        if (currentImageState) {
          if (prevState.isValid && !currentImageState.isValid) {
            currentState.warnings.push({
              type: 'reference_lost',
              imageId,
              message: `Imagem ${imageId} perdeu referência válida`,
              timestamp: now,
              timeSinceLastValid: now - previousState.timestamp
            });
          }
        }
      });

      // Log de warnings e errors
      if (currentState.warnings.length > 0) {
        console.warn('⚠️ [ImageMonitor] Warnings detectados:', {
          count: currentState.warnings.length,
          warnings: currentState.warnings
        });
      }

      if (currentState.errors.length > 0) {
        console.error('❌ [ImageMonitor] Errors detectados:', {
          count: currentState.errors.length,
          errors: currentState.errors
        });
      }

      // Log periódico de estatísticas (apenas se houver mudanças ou warnings)
      if (currentState.warnings.length > 0 || currentState.errors.length > 0) {
        const stats = {
          totalImages: uploadedImages.length,
          validImages: Array.from(currentState.images.values()).filter(s => s.isValid).length,
          invalidImages: Array.from(currentState.images.values()).filter(s => !s.isValid && s.hasImage).length,
          missingReferences: Array.from(currentState.images.values()).filter(s => !s.hasImage).length,
          warnings: currentState.warnings.length,
          errors: currentState.errors.length
        };
        
        console.log('📊 [ImageMonitor] Estatísticas:', stats);
      }

      // Atualizar estado anterior
      previousStateRef.current = currentState;
    }, interval);

    return () => {
      clearInterval(monitorInterval);
    };
  }, [uploadedImages, interval]);

  // Monitorar especificamente a imagem selecionada com mais frequência
  useEffect(() => {
    if (!selectedImageId || selectedImageId === 'base-layer') {
      return;
    }

    const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
    if (!selectedImage) {
      return;
    }

    const quickCheckInterval = setInterval(() => {
      if (!selectedImage.image) {
        console.warn('⚠️ [ImageMonitor] Imagem selecionada sem referência:', selectedImageId);
        return;
      }

      if (!isValidImage(selectedImage.image)) {
        console.warn('⚠️ [ImageMonitor] Imagem selecionada inválida:', {
          imageId: selectedImageId,
          complete: selectedImage.image.complete,
          width: selectedImage.image.naturalWidth,
          height: selectedImage.image.naturalHeight
        });
      }
    }, 5000); // Verificar a cada 5 segundos para a imagem selecionada

    return () => {
      clearInterval(quickCheckInterval);
    };
  }, [selectedImageId, uploadedImages]);
}

